import type { Contact, Recipient, RecipientListId } from 'types/list'

/**
 * Determines the subscription status of a contact, potentially for a specific list.
 *
 * Evaluates whether a contact is subscribed, unsubscribed from a specific list,
 * or globally unsubscribed from all communications.
 * Returns status strings prefixed with numbers to ensure they're sortable in tables.
 *
 * @param contact - The contact object to check status for
 * @param listId - Optional ID of the specific recipient list to check against
 * @returns A sortable status string indicating subscription status
 */
export function getContactStatus(
  contact: Contact,
  listId?: RecipientListId | null,
): '0_SUBSCRIBED' | '1_UNSUBSCRIBED_THIS' | '2_UNSUBSCRIBE_ALL' {
  const { unsubscribes } = contact
  if (!unsubscribes) return '0_SUBSCRIBED'
  // `status` must be sortable (for table)
  let status
  if (unsubscribes.global) {
    status = '2_UNSUBSCRIBE_ALL'
  } else if (listId && unsubscribes.saved_recipients_ids.includes(listId)) {
    status = '1_UNSUBSCRIBED_THIS'
  } else {
    status = '0_SUBSCRIBED'
  }
  return status
}

/**
 * Converts a Contact object to a Recipient format.
 *
 * Takes a Contact object and transforms it into the Recipient format used by the system.
 * Preserves business IDs if available from the contact's DISCO user information.
 *
 * @param contact - The Contact object to convert
 * @returns A Recipient object containing the contact's information
 */
export function contactToRecipient(contact: Contact): Recipient {
  // TODO: support business
  let business_ids: number[] | undefined = undefined
  if (contact.disco_user && contact.disco_user.business_ids) {
    business_ids = contact.disco_user.business_ids
  }
  const recipient = {
    type: 'user',
    email: contact.email,
    name: contact.name,
    contact,
    business_ids,
  } as const
  return recipient
}
